// Safe Markdown subset → React nodes. Never produces HTML strings, so message content can't inject markup.
import { useState, type ReactNode } from "react";

interface Ctx { meName: string | null; knownUsers: Set<string> }

function Spoiler({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return <span className={`spoiler${open ? " open" : ""}`} role="button" tabIndex={0} onClick={() => setOpen(true)}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(true); }}>{children}</span>;
}

const INLINE = /(`[^`\n]+`)|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(~~[^~\n]+~~)|(\|\|[^|\n]+\|\|)|(\*[^*\s][^*\n]*\*)|(_[^_\s][^_\n]*_)|(https?:\/\/[^\s<>()]+[^\s<>().,!?،؟"'])|((?:^|(?<=[\s(]))@(?:everyone|here|[a-z0-9_.]{2,32}))/gi;

function inline(text: string, ctx: Ctx, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0, i = 0;
  for (const m of text.matchAll(INLINE)) {
    const idx = m.index!;
    if (idx > last) out.push(text.slice(last, idx));
    const tok = m[0];
    const k = `${keyBase}-${i++}`;
    if (m[1]) out.push(<code key={k}>{tok.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={k}>{inline(tok.slice(2, -2), ctx, k)}</strong>);
    else if (m[3]) out.push(<u key={k}>{inline(tok.slice(2, -2), ctx, k)}</u>);
    else if (m[4]) out.push(<s key={k}>{inline(tok.slice(2, -2), ctx, k)}</s>);
    else if (m[5]) out.push(<Spoiler key={k}>{inline(tok.slice(2, -2), ctx, k)}</Spoiler>);
    else if (m[6] || m[7]) out.push(<em key={k}>{inline(tok.slice(1, -1), ctx, k)}</em>);
    else if (m[8]) out.push(<a key={k} href={tok} target="_blank" rel="noopener noreferrer nofollow" dir="ltr">{tok}</a>);
    else if (m[9]) {
      const name = tok.slice(1).toLowerCase();
      const isMe = name === "everyone" || name === "here" || name === ctx.meName;
      if (name === "everyone" || name === "here" || ctx.knownUsers.has(name)) out.push(<span key={k} className={`mention${isMe ? " me" : ""}`} dir="auto">{tok}</span>);
      else out.push(tok);
    }
    last = idx + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function renderMarkdown(src: string, ctx: Ctx): ReactNode[] {
  const out: ReactNode[] = [];
  const parts = src.split(/```/);
  parts.forEach((part, pi) => {
    if (pi % 2 === 1) {
      const nl = part.indexOf("\n");
      const lang = nl > 0 && /^[\w+-]{1,20}$/.test(part.slice(0, nl).trim()) ? part.slice(0, nl).trim() : "";
      const code = lang ? part.slice(nl + 1) : part.replace(/^\n/, "");
      out.push(<pre key={`c${pi}`} className="codeblock" dir="ltr"><code data-lang={lang || undefined}>{code.replace(/\n$/, "")}</code></pre>);
      return;
    }
    const lines = part.split("\n");
    let quote: ReactNode[] = [];
    const flushQuote = (k: string) => { if (quote.length) { out.push(<blockquote key={k}>{quote}</blockquote>); quote = []; } };
    lines.forEach((line, li) => {
      const k = `p${pi}-${li}`;
      if (/^> /.test(line)) { quote.push(<div key={k}>{inline(line.slice(2), ctx, k)}</div>); return; }
      flushQuote(`q${k}`);
      if (li > 0 || (pi > 0 && line)) out.push(<br key={`br${k}`} />);
      out.push(...inline(line, ctx, k));
    });
    flushQuote(`q${pi}-end`);
  });
  // Drop a leading <br/> created by the line join.
  while (out.length && (out[0] as any)?.type === "br") out.shift();
  return out;
}

/** True when the message is only emoji (render them large). */
export const isJumbo = (s: string) => s.length <= 24 && /^(?:\p{Extended_Pictographic}|\p{Emoji_Component}|\s|‍|️)+$/u.test(s) && /\p{Extended_Pictographic}/u.test(s);
